// Function index:
//   renderParties(host, parties, memberCap, onPick) — fill the parties list with seat counts.
//   renderWorlds(host, worlds, onEdit, onCreateParty) — fill the worlds list.
//   renderThemed(host, themes, onEnter) — fill the themed-world picker.

import type { PartySummary, ThemedWorldSummary, WorldSummary } from '../../api.js';
import { esc, makeRow, metaLine } from './dom.js';

export function renderParties(
  host: HTMLElement,
  parties: PartySummary[],
  memberCap: number,
  onPick: (partyId: number) => void,
): void {
  host.innerHTML = '';
  if (parties.length === 0) {
    host.appendChild(metaLine(
      'No parties yet — join one by invite, or create a world below and start a party there.',
    ));
    return;
  }
  for (const p of parties) {
    const full = p.member_count >= memberCap;
    const seats = `${p.member_count}/${memberCap} · ${full ? 'full' : 'invite a friend'}`;
    const row = makeRow();
    row.innerHTML = `
      <div>
        <div>${esc(p.party_name)}</div>
        <div class="party-meta">${esc(p.world_name)} · ${esc(p.world_setting)}</div>
      </div>
      <div class="party-meta">${esc(seats)} · invite: ${esc(p.invite_code)}</div>
    `;
    row.addEventListener('click', () => onPick(p.party_id));
    host.appendChild(row);
  }
}

export function renderWorlds(
  host: HTMLElement,
  worlds: WorldSummary[],
  onEdit: (world: WorldSummary) => void,
  onCreateParty: (world: WorldSummary) => void,
): void {
  host.innerHTML = '';
  if (worlds.length === 0) {
    host.appendChild(metaLine('No worlds yet. Click "+ Create a world" to build one.'));
    return;
  }
  for (const w of worlds) {
    const row = makeRow();
    row.style.cursor = 'default';
    row.innerHTML = `
      <div>
        <div>${esc(w.name)}</div>
        <div class="party-meta">${esc(w.setting)} · ${w.grid_width}×${w.grid_height}</div>
      </div>
      <div style="display:flex; gap:6px;">
        <button class="secondary" data-action="edit">Edit</button>
        <button class="secondary" data-action="party">Create party</button>
      </div>
    `;
    row.querySelector('[data-action="edit"]')!.addEventListener('click', () => onEdit(w));
    row.querySelector('[data-action="party"]')!.addEventListener('click', () => onCreateParty(w));
    host.appendChild(row);
  }
}

// Themed-world picker. One card per theme: name, brief geography excerpt
// (from the seeder's vision document), one-click "Enter" that asks the
// server to create-or-reuse a party in that world.
export function renderThemed(
  host: HTMLElement,
  themes: ThemedWorldSummary[],
  onEnter: (theme: string) => void,
): void {
  host.innerHTML = '';
  if (themes.length === 0) {
    host.appendChild(metaLine('No ready-made worlds are available yet — forge your own from the menu to begin.'));
    return;
  }
  for (const t of themes) {
    const row = makeRow();
    const excerpt = t.excerpt
      ? (t.excerpt.length > 200 ? t.excerpt.slice(0, 200) + '…' : t.excerpt)
      : `${t.theme} · ${t.grid_width}×${t.grid_height} hexes`;
    const stats = t.stats
      ? `<div class="party-meta" style="font-size:11px; margin-top:4px;">${t.stats.parties_active_7d} ${t.stats.parties_active_7d === 1 ? 'party' : 'parties'} active this week · ${t.stats.places_discovered} places discovered · ${t.stats.laws_count} laws inscribed</div>`
      : '';
    const headlines = (t.headlines ?? []).slice(0, 3)
      .map((h) => `<div class="party-meta" style="font-size:11px;">• ${esc(h)}</div>`)
      .join('');
    row.innerHTML = `
      <div style="flex:1; min-width:0;">
        <div style="color:var(--color-accent); margin-bottom:3px;">${esc(t.name)}</div>
        <div class="party-meta" style="font-size:12px; line-height:1.4;">${esc(excerpt)}</div>
        ${stats}${headlines}
      </div>
      <button type="button" data-action="enter" style="margin-left:10px; flex-shrink:0;">Enter</button>
    `;
    row.querySelector('[data-action="enter"]')!.addEventListener('click', (e) => { e.stopPropagation(); onEnter(t.theme); });
    row.addEventListener('click', () => onEnter(t.theme));
    host.appendChild(row);
  }
}
